import { batch } from 'react-redux';

// project imports
import gestopAPI from 'utils/gestopAPI';
import { dispatch } from 'store/index';
import { errorProcessor } from 'utils/processors';
import { crmContractTypeActions } from './slice';
import capabilities from './capabilities';
import createImportApi from 'utils/importAPI';

const contractTypeImportApi = createImportApi({
    actions: crmContractTypeActions,
    capabilities,
    endpoint: 'crm/contracttypes',
    templateFileName: 'ContractTypeImportTpl'
});
export const ApiContractTypesImportCollection = contractTypeImportApi.importCollection;
export const ApiContractTypesDownloadImportModelItem = contractTypeImportApi.downloadImportModel;

export const ApiContractTypesGetCollection = async ({
    filters = [],
    orderBy = [],
    groupBy = [],
    page = 1,
    limit = 10,
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmContractTypeActions.startLoading());
    }

    await gestopAPI
        .post('crm/contracttypes/list', { filters, orderBy, groupBy, page, limit, includeSafeDeleted }, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(crmContractTypeActions.setList(response?.data?.list));
                    dispatch(crmContractTypeActions.setCount(response?.data?.count));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data?.list);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmContractTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiContractTypesCountCollection = async ({
    filters = [],
    includeSafeDeleted = false,
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    await gestopAPI
        .post('crm/contracttypes/count', { filters, includeSafeDeleted }, config)
        .then((response) => {
            if (typeof successCallback === 'function') {
                successCallback(response?.data?.count);
            }
        })
        .catch((error) => {
            const errorMsg = errorProcessor(error?.response?.data);

            if (typeof errorCallback === 'function') {
                errorCallback(errorMsg);
            }
        });
};

export const ApiContractTypesPostCollection = async ({
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmContractTypeActions.startLoading());
    }

    await gestopAPI
        .post('crm/contracttypes', data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(crmContractTypeActions.insertItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmContractTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiContractTypesDeleteCollection = async ({
    ids = [],
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmContractTypeActions.startLoading());
    }

    await gestopAPI
        .post('crm/contracttypes/safedelete', ids, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(crmContractTypeActions.deleteItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmContractTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiContractTypesExportCollection = async ({
    exportType = 'allRows',
    format = 'pdf',
    columns = [],
    filters = [],
    orderBy = [],
    groupBy = [],
    successCallback = () => {},
    errorCallback = () => {},
    config = {}
}) => {
    if (capabilities.app.includes('exporter')) {
        dispatch(crmContractTypeActions.startExporting());
    }

    await gestopAPI
        .post('crm/contracttypes/export', { exportType, format, columns, filters, orderBy, groupBy }, { ...config, responseType: 'blob' })
        .then((response) =>
            batch(() => {
                window.open(URL.createObjectURL(response?.data));

                if (capabilities.app.includes('exporter')) {
                    dispatch(crmContractTypeActions.stopExporting());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('exporter')) {
                    dispatch(crmContractTypeActions.stopExporting());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmContractTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiContractTypesPutItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmContractTypeActions.startLoading());
    }

    await gestopAPI
        .put(`crm/contracttypes/${id}`, data, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(crmContractTypeActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmContractTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiContractTypesPatchItem = async ({
    id,
    data = {},
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmContractTypeActions.startLoading());
    }

    await gestopAPI
        .patch(`crm/contracttypes/${id}`, data, {
            ...config,
            headers: { ...config?.headers, 'Content-Type': 'application/merge-patch+json' } // Specific content type for PATCH requests
        })
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(crmContractTypeActions.updateItems(response?.data));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmContractTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiContractTypesDeleteItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmContractTypeActions.startLoading());
    }

    await gestopAPI
        .post(`crm/contracttypes/safedelete/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(crmContractTypeActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmContractTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiContractTypesRestoreDeletedItem = async ({
    id,
    successCallback = () => {},
    errorCallback = () => {},
    updateStore = true,
    config = {}
}) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmContractTypeActions.startLoading());
    }

    await gestopAPI
        .post(`crm/contracttypes/restoresafedeleted/${id}`, {}, config)
        .then((response) =>
            batch(() => {
                if (updateStore) {
                    dispatch(crmContractTypeActions.deleteItems(id));
                }
                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmContractTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};

export const ApiContractTypesGetItem = async ({ id, successCallback = () => {}, errorCallback = () => {}, config = {} }) => {
    if (capabilities.app.includes('loader')) {
        dispatch(crmContractTypeActions.startLoading());
    }

    await gestopAPI
        .get(`crm/contracttypes/${id}`, config)
        .then((response) =>
            batch(() => {
                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (typeof successCallback === 'function') {
                    successCallback(response?.data);
                }
            })
        )
        .catch((error) =>
            batch(() => {
                const errorMsg = errorProcessor(error?.response?.data);

                if (capabilities.app.includes('loader')) {
                    dispatch(crmContractTypeActions.stopLoading());
                }
                if (capabilities.app.includes('error-logger')) {
                    dispatch(crmContractTypeActions.logError(errorMsg));
                }
                if (typeof errorCallback === 'function') {
                    errorCallback(errorMsg);
                }
            })
        );
};
